<!-- ModalNuevoPedido.vue — Wizard 3 pasos con persistencia de borrador -->
<template>
  <div class="modal fade" id="modalNuevoPedido" tabindex="-1" ref="el">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content divisan-modal border-0">
        <!-- Header + stepper -->
        <div class="modal-header border-0 pb-0 flex-column align-items-start">
          <div class="d-flex w-100 justify-content-between align-items-center mb-3">
            <div>
              <h5 class="fw-bold text-cafe mb-0">Nuevo pedido</h5>
              <!-- Indicador borrador activo -->
              <small v-if="borradorActivo" class="text-verde fw-bold">
                <i class="bi bi-cloud-check me-1"></i>
                <span class="texto-largo">Borrador guardado automáticamente</span>
                <span class="texto-corto">Borrador guardado</span>
              </small>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <button
                v-if="borradorActivo"
                class="btn btn-descartar btn-sm"
                @click="handleDescartar"
                title="Descartar borrador y empezar de nuevo"
              >
                <i class="bi bi-trash me-1"></i>
                <span class="texto-largo">Descartar borrador</span>
                <span class="texto-corto">Descartar</span>
              </button>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
          </div>
          <div class="stepper w-100 mb-2">
            <div
              v-for="(s, i) in pasos"
              :key="i"
              :class="['step', { active: paso === i + 1, done: paso > i + 1 }]"
            >
              <div class="step-circle">
                <i v-if="paso > i + 1" class="bi bi-check-lg"></i>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="step-label">{{ s }}</span>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="modal-body pt-2">
          <PedidoPaso1
            v-if="paso === 1"
            v-model:formPedido="formPedido"
            v-model:pdfFile="pdfFile"
            :catClientes="catClientes"
            :fechaLimite="fechaLimite"
          />
          <PedidoPaso2
            v-if="paso === 2"
            :items="items"
            :itemNuevo="itemNuevo"
            :catArticulos="catArticulos"
            :analizando="analizando"
            :itemsDesdeIa="itemsDesdeIa"
            :itemsConMatch="itemsConMatch"
            :itemsSinMatch="itemsSinMatch"
            :totalPiezas="totalPiezas"
            :codigoArticulo="codigoArticulo"
            :descripcionArticulo="descripcionArticulo"
            :filtrarArticulos="filtrarArticulos"
            :asignarArticuloItem="asignarArticuloItem"
            :agregarItem="agregarItem"
            :quitarItem="quitarItem"
          />
          <PedidoPaso3 v-if="paso === 3" v-model:proyectos="proyectos" />
        </div>

        <!-- Footer navegación -->
        <div class="modal-footer border-0 pt-0">
          <button
            v-if="paso > 1"
            class="btn btn-exportar me-auto"
            @click="retroceder"
            :disabled="guardando || analizando"
          >
            <i class="bi bi-arrow-left me-1"></i>Anterior
          </button>

          <!-- Paso 1 → 2 -->
          <button
            v-if="paso === 1"
            class="btn btn-nuevo"
            @click="avanzar"
            :disabled="!puedeAvanzar"
          >
            {{ pdfFile ? "Analizar PDF y continuar" : "Continuar" }}
            <i class="bi bi-arrow-right ms-1"></i>
          </button>

          <!-- Paso 2 → 3 -->
          <button
            v-if="paso === 2"
            class="btn btn-nuevo"
            @click="avanzar"
            :disabled="analizando || items.filter((i) => i.articulo_id).length === 0"
          >
            Continuar
            <i class="bi bi-arrow-right ms-1"></i>
          </button>

          <!-- Paso 3 → guardar -->
          <button
            v-if="paso === 3"
            class="btn btn-nuevo"
            @click="handleGuardar"
            :disabled="guardando"
          >
            <span v-if="guardando" class="spinner-border spinner-border-sm me-2"></span>
            {{ guardando ? "Guardando..." : "Guardar pedido" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import PedidoPaso1 from "./PedidoPaso1.vue";
import PedidoPaso2 from "./PedidoPaso2.vue";
import PedidoPaso3 from "./PedidoPaso3.vue";
import { usePedidoForm } from "./composables/usePedidoForm.js";
import Swal from "sweetalert2";

const props = defineProps({
  catClientes: { type: Array, default: () => [] },
  catArticulos: { type: Array, default: () => [] },
});
const emit = defineEmits(["guardado"]);

const el = ref(null);
let modalInstance = null;

const catArticulosRef = computed(() => props.catArticulos);

const {
  paso,
  pasos,
  guardando,
  analizando,
  pdfFile,
  itemsDesdeIa,
  formPedido,
  items,
  itemNuevo,
  proyectos,
  fechaLimite,
  puedeAvanzar,
  totalPiezas,
  itemsConMatch,
  itemsSinMatch,
  codigoArticulo,
  descripcionArticulo,
  filtrarArticulos,
  asignarArticuloItem,
  agregarItem,
  quitarItem,
  avanzar,
  retroceder,
  guardarPedido,
  reset,
  activarAutoGuardado,
  stopAutoGuardado,
  restaurarBorrador,
} = usePedidoForm(catArticulosRef);

const borradorActivo = ref(false);

onMounted(() => {
  modalInstance = new Modal(el.value);

  el.value.addEventListener("hidden.bs.modal", () => {
    stopAutoGuardado();
    borradorActivo.value = false;
  });
});

async function abrir() {
  await reset();

  const restaurado = await restaurarBorrador();

  activarAutoGuardado();
  borradorActivo.value = true;

  if (restaurado && items.value.length > 0) paso.value = 2;

  modalInstance.show();
}
defineExpose({ abrir });

async function handleGuardar() {
  await guardarPedido(() => {
    borradorActivo.value = false;
    modalInstance.hide();
    emit("guardado");
  });
}

async function handleDescartar() {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Descartar borrador?",
    text: "Se perderán todos los datos ingresados.",
    showCancelButton: true,
    confirmButtonColor: "#c0392b",
    cancelButtonColor: "var(--d-green)",
    confirmButtonText: "Sí, descartar",
    cancelButtonText: "Cancelar",
  });
  if (!isConfirmed) return;
  await reset(true);
  borradorActivo.value = false;
}
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
@media (max-width: 1000px) {
  .divisan-modal {
    margin-top: 12rem;
  }
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-exportar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-exportar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-descartar {
  background: none;
  border: 1px solid #c0392b;
  color: #c0392b;
  font-size: 0.78rem;
  font-weight: 700;
  border-radius: 8px;
  padding: 3px 10px;
}
/* Textos largos del header: en móvil chico ("Borrador guardado
   automáticamente" / "Descartar borrador") no caben junto al stepper. */
.texto-corto {
  display: none;
}
@media (max-width: 425px) {
  .texto-largo {
    display: none;
  }
  .texto-corto {
    display: inline;
  }
}

.btn-descartar:hover {
  background: #fdecea;
}

.stepper {
  display: flex;
  align-items: center;
}
.step {
  display: flex;
  align-items: center;
  flex: 1;
}
.step:not(:last-child)::after {
  content: "";
  flex: 1;
  height: 2px;
  background: rgba(64, 34, 8, 0.2);
  margin: 0 8px;
}
.step.done::after {
  background: var(--d-green);
}

.step-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.85rem;
  border: 2px solid rgba(64, 34, 8, 0.25);
  color: rgba(64, 34, 8, 0.4);
  background: white;
  flex-shrink: 0;
}
.step.active .step-circle {
  border-color: var(--d-brown);
  background: var(--d-brown);
  color: var(--d-cream);
}
.step.done .step-circle {
  border-color: var(--d-green);
  background: var(--d-green);
  color: var(--d-cream);
}
.step-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(64, 34, 8, 0.4);
  white-space: nowrap;
  margin-left: 6px;
}
.step.active .step-label {
  color: var(--d-brown);
}
.step.done .step-label {
  color: var(--d-green);
}
</style>
